// Location pages content. Each town has genuinely distinct copy (no thin/duplicate content).
export const LOCATIONS = [
  {
    slug: "accountants-tamworth",
    town: "Tamworth",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Tamworth",
    h1: "Accountants in Tamworth",
    metaTitle: "Accountants in Tamworth — Xero Platinum Partner",
    metaDescription:
      "Accountants in Tamworth based in Amington. A Xero Platinum Partner providing bookkeeping, VAT, payroll, year-end accounts and outsourced finance support for growing businesses across Tamworth and Staffordshire.",
    opening:
      "CASS is a firm of accountants based in Amington, Tamworth, working with growing businesses across the town and the wider Staffordshire area. As a Xero Platinum Partner we handle bookkeeping, VAT, payroll and year-end accounts — and the financial reporting and outsourced finance support that comes after them. In person from our Amington office, or remotely. Whichever suits you.",
    sections: [
      {
        heading: "What Tamworth businesses come to us for",
        body: ["Most start with one thing that has become a problem, and stay for the rest."],
        items: [
          { label: "Bookkeeping", to: "/services/bookkeeping", note: "Kept up to date in Xero, reconciled, and actually useful. This is where almost every relationship begins." },
          { label: "VAT and MTD for VAT", to: "/services/vat", note: "Returns prepared and filed on time, with the scheme reviewed rather than assumed." },
          { label: "Payroll", to: "/services/payroll", note: "Including auto-enrolment, starters, leavers and the awkward months." },
          { label: "Limited Company Year End Accounts", to: "/services/ltd-co-accounts", note: "Filed to deadline, explained in plain English before they are filed." },
          { label: "Financial Reports", to: "/services/management-accounts-financial-reporting", note: "Monthly or quarterly management accounts, so you know how the year is going while you can still change it." },
          { label: "Strategic Planning and Insights", to: "/services/tax-planning", note: "Budgets, forecasts and the commercial effect of a decision before you commit to it." },
          { label: "Outsourced Finance and Virtual FD", to: "/services/outsourced-finance", note: "The whole finance function, for businesses that have outgrown doing it themselves." },
          { label: "Moving to Xero", to: "/services/set-up-and-implementation", note: "Migration handled properly, with the history brought across." },
          { label: "Xero support", to: "/services/support", note: "For businesses already on Xero that are not getting what they should from it." },
        ],
      },
      {
        heading: "Who we are a good fit for",
        body: [
          "Growing businesses in and around Tamworth that want an ongoing finance relationship — a named client manager who knows the business, records kept up to date, compliance filed on time and reporting you can act on.",
          "We are honest about the other side of that. We do not normally provide compliance work in isolation: filing a VAT return for records nobody maintains does not serve anyone well, and we would rather say so than take the fee. The exception is ACSP identity verification, which we do provide as a standalone service.",
        ],
      },
      {
        heading: "What working with us looks like",
        body: [
          "Your records are kept up to date in Xero, so you and we are looking at the same numbers at the same time. Compliance is filed on time without you chasing it. You get reporting you can act on, and a named client manager who knows your business rather than a different voice each time you call. Over 25 years of experience sits behind that, and Paul Barnes, who founded CASS, is still the person you will meet.",
        ],
      },
    ],
    intro: [
      "CASS is proud to call Tamworth home. From our office in Amington, we support businesses right across the town — from established family firms to fast-growing start-ups — with the day-to-day accounting and forward-looking advice they need to thrive.",
      "Being local matters. We know the Tamworth business community, we're easy to reach, and we're the sort of accountants you can actually pick up the phone to. As a Xero Platinum Partner, we pair leading cloud technology with genuinely proactive support.",
    ],
    nearby: ["Amington", "Glascote", "Wilnecote", "Fazeley", "Bolehall", "Kettlebrook"],
    faqs: [
      {
        question: "Do you meet Tamworth clients face to face?",
        answer:
          "Yes. Our office is in Amington, Tamworth, so we're always happy to meet in person. Many clients also prefer video calls for convenience — whatever works best for you.",
      },
      {
        question: "Do I need to switch to Xero to work with you?",
        answer:
          "In practice, yes — CASS is a 100% Xero practice, and that is deliberate rather than a sales preference. It is how we can keep every client's records current and see the same numbers you do. We're a Xero Platinum Partner, and if moving across makes sense we handle the migration for you, history and all.",
      },
      {
        question: "How much do accountants in Tamworth cost?",
        answer:
          "We work on fixed monthly fees tailored to what you actually need — no surprise bills. You don't have to ask us what that is, either: build your package on our pricing page and you'll see your own figure in about two minutes, with no email address needed.",
      },
      {
        question: "I only need my VAT returns doing. Can you help?",
        answer:
          "Usually as part of an ongoing arrangement rather than on its own. Filing returns against records nobody maintains serves no one well, so we would rather say so than take the fee. Tell us what you need on a Discovery Call and we'll be straight with you about whether we're the right fit.",
      },
      {
        question: "What does working with you look like month to month?",
        answer:
          "Records up to date in Xero, compliance filed on time, reporting you can act on, and a named client manager who knows your business. More than numbers, in practice rather than as a slogan.",
      },
    ],
  },
  {
    slug: "accountants-sutton-coldfield",
    town: "Sutton Coldfield",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Sutton Coldfield",
    metaDescription:
      "Xero accountants and outsourced finance for Royal Sutton Coldfield — from consultancies around Mere Green to trade and industrial businesses at Minworth.",
    intro: [
      "Royal Sutton Coldfield is one of the most distinctive business communities in the West Midlands: a town of professional practices, consultancies and owner-managed companies that happens to sit inside the City of Birmingham. We work with businesses right across it, from our base a short drive away in Tamworth.",
      "It's a place of two quite different economies, and we're comfortable in both. Around Mere Green, Four Oaks and the Parade you'll find consultancies, dental and healthcare practices, professional firms and independent retailers — mostly director-run, often with salary and dividends to think about. Out at Minworth it's trade counters, light industrial units and distribution, where the questions are about margins, plant and keeping VAT straight.",
      "There's a practical quirk worth knowing about too. Because Sutton Coldfield falls under Birmingham City Council for business rates and licensing while having its own town council, we've had plenty of clients arrive unsure which authority they're actually dealing with. We'll sort it out with you rather than leave you to guess.",
      "Being nine miles up the A38 and A5127 — or fifteen minutes on the M6 Toll — means we're close enough to sit down with you when it matters, without pretending to be something we're not. We don't have a Sutton Coldfield office. What we have is a team that will actually pick up the phone, and Xero set up so you can see your numbers whenever you want to, wherever you are.",
    ],
    nearby: ["Mere Green", "Four Oaks", "Walmley", "Wylde Green", "Boldmere", "Streetly", "Minworth", "New Oscott"],
    faqs: [
      {
        question: "Do you work with company directors on salary and dividends?",
        answer:
          "Absolutely — it's one of the most common set-ups we support in Sutton Coldfield, where a great many businesses are owner-run. We'll structure your salary and dividends tax-efficiently, keep an eye on your director's loan account, and make sure there are no unwelcome surprises at year end.",
      },
      {
        question: "Are you local to Sutton Coldfield?",
        answer:
          "We're based in Amington, Tamworth — roughly nine miles away up the A38, or about fifteen minutes on the M6 Toll. We'll say it plainly: we don't have an office in Sutton Coldfield. We're close enough to meet you in person when it's useful, and set up so that everything else works just as well over Xero and video call.",
      },
      {
        question: "Do you support professional practices — consultancies, dental, healthcare?",
        answer:
          "Yes, and they make up a good share of the Sutton Coldfield businesses we look after. Practices tend to have their own rhythm: associate and locum arrangements, equipment finance, staff who move between sites. We're used to all of it, and to explaining what it means for your profit rather than just recording it.",
      },
      {
        question: "We're a trade or industrial business at Minworth. Is that your sort of client?",
        answer:
          "Very much so. Minworth's trade counters, workshops and distribution units are a different beast to a consultancy on the Parade — stock, plant, subcontractors, vehicles and tighter margins. We'll get the bookkeeping right underneath it and then show you which jobs and lines actually make money.",
      },
      {
        question: "Which council do we deal with — Birmingham or Sutton Coldfield?",
        answer:
          "Business rates, licensing and planning go through Birmingham City Council, because Sutton Coldfield sits within the city. Royal Sutton Coldfield Town Council handles more local matters. It catches people out regularly, so if you're not sure who you should be talking to, just ask us.",
      },
      {
        question: "Can you take over from my current accountant?",
        answer:
          "Yes. Switching is straightforward — we handle the handover with your previous accountant, including part-way through a year, and make sure nothing slips through the cracks.",
      },
    ],
  },
  {
    slug: "accountants-burton-upon-trent",
    town: "Burton-upon-Trent",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Burton-upon-Trent",
    metaDescription:
      "Xero accountants and outsourced finance for Burton-upon-Trent businesses — brewing, manufacturing, logistics and more. Bookkeeping, VAT, payroll and business advice.",
    intro: [
      "Burton-upon-Trent's proud manufacturing, brewing and logistics heritage has created a business base that values reliability and precision — and that's exactly what we bring to your finances.",
      "We help Burton businesses tighten cashflow, understand true profitability and plan for growth, with cloud accounting that gives you real-time visibility rather than accounts that arrive months too late.",
    ],
    nearby: ["Branston", "Stretton", "Winshill", "Stapenhill", "Horninglow"],
    faqs: [
      {
        question: "Do you work with manufacturing and logistics businesses?",
        answer:
          "Yes — Burton's manufacturing, brewing and logistics firms are a big part of who we help. We're comfortable with stock, job costing and the margins that matter to these sectors.",
      },
      {
        question: "Can you help with cashflow forecasting?",
        answer:
          "Definitely. Real-time cashflow visibility is central to how we work with Burton-upon-Trent businesses, so you always know what's coming and can plan for it.",
      },
      {
        question: "How quickly can I get my accounts and numbers?",
        answer:
          "With cloud accounting, your figures are always up to date rather than arriving months after year-end. You get real-time visibility whenever you need it.",
      },
    ],
  },
  {
    slug: "accountants-birmingham",
    town: "Birmingham",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Birmingham",
    h1: "Accountants in Birmingham",
    metaTitle: "Accountants in Birmingham — Bookkeeping, Accounts and Outsourced Finance",
    metaDescription:
      "Accountants for Birmingham businesses — bookkeeping, VAT, payroll, year-end accounts, financial reports and outsourced finance. An outsourced finance team seventeen miles from the city, working in live Xero.",
    opening:
      "CASS works with Birmingham businesses as their accountants and, for many of them, as their whole finance function — bookkeeping, VAT, payroll and year-end accounts, and the management accounts, forecasting and Virtual FD support that come after them. We are seventeen miles up the A38 in Amington, Tamworth, which is precisely why we can give you more finance capability for the same fee than a city-centre address can.",
    sections: [
      {
        heading: "An accountant — the whole compliance side, done properly and on time",
        body: [
          "None of it is the interesting part, and all of it has to be right before anything else is worth doing.",
        ],
        items: [
          { label: "Bookkeeping", to: "/services/bookkeeping", note: "Kept current in live Xero." },
          { label: "VAT and MTD for VAT", to: "/services/vat", note: "Prepared and filed, with the scheme reviewed rather than assumed." },
          { label: "Payroll", to: "/services/payroll", note: "Including auto-enrolment." },
          { label: "Limited Company Year End Accounts", to: "/services/ltd-co-accounts", note: "And the corporation tax return, explained before they are filed." },
          { label: "Personal Tax and Self Assessment", to: "/services/self-assessment", note: "For the directors behind the company." },
        ],
      },
      {
        heading: "The bookkeeping itself, taken off your hands",
        body: [
          "This is the most common first conversation, particularly for agencies and consultancies: books weeks behind, VAT a scramble every quarter, and no way of telling which clients or projects actually make money.",
          "We maintain the books weekly or monthly depending on volume, reconcile in live Xero, capture receipts through Dext, and set up tracking so project and client profitability is visible rather than guessed. If the books need rescuing before they can be maintained, that is a clean-up, and we do that too.",
        ],
        links: [{ label: "Bookkeeping services in Birmingham", to: "/bookkeeping-services-birmingham" }],
      },
      {
        heading: "A finance function, without the hires",
        body: [
          "For businesses that need finance capability rather than more compliance — the reporting, the forecasting and someone to think the numbers through with.",
        ],
        items: [
          { label: "Financial Reports", to: "/services/management-accounts-financial-reporting", note: "Monthly or quarterly management accounts." },
          { label: "Cashflow Forecasting", to: "/services/cash-flow-forecasting", note: "What the bank balance does next, not what it did." },
          { label: "Strategic Planning and Insights", to: "/services/tax-planning", note: "Budgets, scenarios and the commercial effect of a decision before you commit to it." },
          { label: "Outsourced Finance and Virtual FD", to: "/services/outsourced-finance", note: "All of it together, with a finance director's input at a fraction of the salary." },
        ],
      },
      {
        heading: "How we work with a business in the city",
        body: [
          "Everything runs in live Xero, so you and we are looking at the same numbers at the same time rather than waiting for a monthly pack. You get a named client manager who knows your business. We come into Birmingham when a face-to-face genuinely helps, and we don't pretend a city-centre office would make your numbers better.",
          "We are a Xero Platinum Partner and a 100% Xero practice, which is how we can keep every client current — migrations off desktop Sage for older manufacturers, connected apps for agencies billing time, and reporting that is current rather than historic.",
        ],
        links: [{ label: "If Xero itself is the reason you are looking, start here", to: "/xero-accountants-birmingham" }],
      },
      {
        heading: "Who we are a good fit for",
        body: [
          "Birmingham businesses, broadly one to ten million in turnover, that want an ongoing finance relationship. We do not normally provide compliance work in isolation — filing returns against records nobody maintains serves no one — with ACSP identity verification the stated exception.",
        ],
      },
    ],
    intro: [
      "Birmingham has no shortage of accountants, so it is worth being clear about what we are and are not. We are not a city-centre firm with a Colmore Row address and a partner you meet once a year. We are an outsourced finance team, seventeen miles up the A38 in Tamworth, working with Birmingham businesses that need finance capability rather than compliance.",
      "That distinction matters because of who tends to come to us. A Jewellery Quarter manufacturer, a Digbeth agency, an engineering business in Tyseley or Aston — companies turning over one to ten million, too big to run on the owner's instinct and a year-end set of accounts, but nowhere near ready to pay for a full-time finance director. That gap is precisely what a Virtual or Fractional FD arrangement is for: proper management accounts, cashflow forecasting and someone to think the numbers through with, at a fraction of the cost of the hire.",
      "There is a very Birmingham cost we spend a lot of time on, too: the Clean Air Zone. If your vans, HGVs or staff vehicles cross into the city regularly, the daily charges are a real and growing line in your accounts — and they are treated differently depending on whether the vehicle is yours, leased, or an employee's. We make sure they are captured and coded properly rather than disappearing into a general overhead where nobody can see what the city is actually costing you.",
      "We also do a lot of Xero work here, as a Xero Platinum Partner — migrations off desktop Sage for older manufacturers, connected apps for agencies billing time, and reporting that means the numbers are current rather than historic. Being outside the city is not a disadvantage in that world; if anything, it is why we can be cheaper than the firm with the office in the professional quarter.",
    ],
    nearby: ["Colmore Row", "Jewellery Quarter", "Digbeth", "Aston", "Tyseley", "Edgbaston", "Erdington", "Kings Heath"],
    faqs: [
      {
        question: "We're too small for a finance director but the numbers are getting away from us. What do we do?",
        answer:
          "That's the Virtual or Fractional FD conversation, and it's the most common reason Birmingham businesses come to us. You get monthly management accounts, cashflow forecasting and a regular session with someone who knows your business and will tell you what the figures mean — without the salary, the recruitment or the commitment of a full-time hire.",
      },
      {
        question: "Do we need an accountant with a Birmingham office?",
        answer:
          "Honestly, no — and we don't have one, so we would say that. What matters is whether they respond when you call and whether they understand your business. City-centre premises are a cost that ends up in your fee. We're seventeen miles away on the A38 and we'll come to you when a face-to-face genuinely helps.",
      },
      {
        question: "How should we handle Clean Air Zone charges?",
        answer:
          "As their own cost line, not buried in motor expenses. Treatment depends on whether the vehicle is company-owned, leased or an employee's, and whether the journey is genuinely business. Coded properly, you can see exactly what operating in the city costs you — which occasionally changes how businesses schedule their work.",
      },
      {
        question: "Can you migrate us from Sage to Xero?",
        answer:
          "Yes — we're a Xero Platinum Partner and it's one of the most frequent jobs we do for Birmingham manufacturers who've run desktop Sage for years. We bring the history across, reconcile every opening balance, and keep the old system available read-only until you're comfortable.",
      },
      {
        question: "We're an agency billing time, not selling products. Is that a problem?",
        answer:
          "Not at all. For agencies and consultancies the focus shifts to work in progress, recoverability and utilisation rather than stock. We connect Xero to your time and project tools so the reporting reflects chargeable work, which is a genuinely different build to a manufacturing set-up.",
      },
      {
        question: "What does it cost?",
        answer:
          "A fixed monthly fee agreed before we start, based on what you actually need, and published — build your package on the pricing page and you'll see it in about two minutes. No hourly billing, no charge for asking a question, and no bill arriving because a phone call ran long.",
      },
      {
        question: "Can you just do our bookkeeping, or does it have to be everything?",
        answer:
          "Bookkeeping on its own is one of the most common ways businesses start with us, and for many it stays that way. What we don't do is compliance in isolation — filing returns against records nobody maintains — so the bookkeeping comes with the relationship rather than as a detached service.",
      },
    ],
  },
  {
    slug: "accountants-atherstone",
    town: "Atherstone",
    county: "Warwickshire",
    headline: "Accountants & Outsourced Finance in Atherstone",
    metaDescription:
      "Local accountants and outsourced finance for Atherstone businesses. Xero Platinum Partner offering bookkeeping, VAT, payroll, tax and proactive advice.",
    intro: [
      "Right on our doorstep along the A5, Atherstone is a market town with a strong base of independent traders, logistics operators and family businesses — many of whom we're proud to count as clients.",
      "We make running the finance side of your business simpler: accurate bookkeeping, tax handled properly, and a finance partner close by who understands the realities of running a business here.",
    ],
    nearby: ["Mancetter", "Grendon", "Baddesley Ensor", "Hartshill", "Witherley"],
    faqs: [
      {
        question: "Do you work with sole traders and small family businesses?",
        answer:
          "Yes — independent traders and family businesses in Atherstone are exactly who we love to help, whether you're a sole trader or a limited company.",
      },
      {
        question: "How close are you to Atherstone?",
        answer:
          "We're just along the A5 in Tamworth, so we're genuinely local. We're happy to meet in person or work remotely, whichever suits you.",
      },
      {
        question: "Can you handle my tax return as well as bookkeeping?",
        answer:
          "Yes. We can look after everything from day-to-day bookkeeping to VAT, payroll and your tax returns, so it's all handled in one place.",
      },
    ],
  },
  {
    slug: "accountants-nuneaton",
    town: "Nuneaton",
    county: "Warwickshire",
    headline: "Accountants & Outsourced Finance in Nuneaton",
    metaDescription:
      "Xero accountants and outsourced finance for Nuneaton businesses — engineering, retail, manufacturing and services. Bookkeeping, VAT, payroll and business growth advice.",
    intro: [
      "As Warwickshire's largest town, Nuneaton has a diverse economy spanning engineering, retail, distribution and services. We work with owner-managed businesses across all of them.",
      "Our focus is simple: give you the clarity to make better decisions. With cloud accounting and quarterly strategic reviews, you'll always know where your business stands and where it's heading.",
    ],
    nearby: ["Bedworth", "Bulkington", "Attleborough", "Weddington", "Stockingford"],
    faqs: [
      {
        question: "What are the quarterly strategic reviews?",
        answer:
          "They're regular sit-downs where we go through your numbers and what they mean for your Nuneaton business — helping you make decisions based on facts, not guesswork.",
      },
      {
        question: "Do you work with owner-managed businesses?",
        answer:
          "Yes, that's our sweet spot. From engineering to retail and services, we support owner-managed Nuneaton businesses of all shapes and sizes.",
      },
      {
        question: "Is everything done online or can we meet?",
        answer:
          "Both. We use cloud accounting for real-time numbers, but we're always happy to meet or jump on a call whenever you'd like to talk things through.",
      },
    ],
  },
  {
    slug: "accountants-coventry",
    town: "Coventry",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Coventry",
    metaDescription:
      "Outsourced finance team and Xero Platinum Partner for Coventry businesses — automotive, engineering, tech and services. Bookkeeping, VAT, payroll and strategic advice.",
    intro: [
      "Coventry's reinvention as a hub for automotive innovation, engineering and technology has created a wave of ambitious, growing businesses — the kind that benefit most from a proactive finance partner.",
      "We help Coventry founders and directors move beyond compliance: understanding margins, forecasting cashflow and planning investment, so growth is built on facts rather than hope.",
    ],
    nearby: ["City Centre", "Kenilworth", "Bedworth", "Longford", "Canley"],
    faqs: [
      {
        question: "Do you support tech and engineering start-ups?",
        answer:
          "Yes. Coventry's automotive, engineering and tech businesses are a natural fit for us — we help with margins, forecasting and planning investment as you scale.",
      },
      {
        question: "Can you act like an outsourced finance director?",
        answer:
          "That's exactly what many Coventry clients use us for. Beyond the day-to-day, we provide the strategic input of a finance director without the full-time cost.",
      },
      {
        question: "Do you offer fixed monthly fees?",
        answer:
          "Yes — fixed monthly fees tailored to your needs, so you get senior expertise with no surprise bills. Our pricing is published too: build your package on the pricing page and you'll see the figure for your Coventry business in about two minutes.",
      },
    ],
  },
  {
    slug: "accountants-lichfield",
    town: "Lichfield",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Lichfield",
    metaDescription:
      "Accountants and outsourced finance for Lichfield — from professional firms and independents in the city centre to the manufacturers and distributors at Fradley Park.",
    intro: [
      "Lichfield is ten minutes up the A5127 from our office in Amington, and it is genuinely the local business community we know best after Tamworth's own. We have clients in the city centre, out at Fradley Park, and in the villages across the district.",
      "It is not one economy, though, and treating it as one is where generic advice falls down. In the city centre — Bore Street, the Market Square, Bird Street — you have professional practices, independent retailers, cafés and hospitality businesses whose trade rises and falls with the seasons. The Bower, the Christmas market, the cathedral's own calendar and the summer visitors all move the numbers. Cashflow forecasting matters far more here than it does for a business with steady monthly income, because a good August does not pay for a quiet February on its own.",
      "Fradley Park is a completely different proposition: distribution, manufacturing and logistics operating at a scale that has more in common with the M6 corridor than with the cathedral close. Higher transaction volumes, stock, plant, and often shift-based payroll. We work with both, and we do not pretend the same monthly pack suits each of them.",
      "There is a practical Lichfield point worth flagging too. A good number of city-centre premises are listed or sit within the conservation area, which changes how you treat improvement and repair spend for tax — some of it is capital, some allowable, and the line is not always where owners assume. It is worth a conversation before the work starts rather than after the invoice lands. And because Lichfield District Council covers Burntwood and the surrounding villages as well as the city, business rates and reliefs are handled from one place, which at least keeps that part simple.",
    ],
    nearby: ["Burntwood", "Shenstone", "Whittington", "Streethay", "Boley Park", "Fradley", "Alrewas", "Armitage"],
    faqs: [
      {
        question: "Our trade is seasonal. Can you help us plan for the quiet months?",
        answer:
          "That is exactly what cashflow forecasting is for, and for Lichfield city-centre businesses it is usually the most valuable thing we do. We build a rolling forecast from your actual trading pattern — the events, the visitor season, the quiet weeks in the new year — so you can see the dip coming months out and decide what to do about it while you still have options.",
      },
      {
        question: "We're at Fradley Park, not in the city. Do you work with businesses like ours?",
        answer:
          "Yes, and it's a different job to a city-centre practice. Distribution and manufacturing bring higher transaction volumes, stock, plant and often shift-based payroll. We set the bookkeeping up for that volume and report on margin per line or per contract, rather than handing you a set of accounts once a year.",
      },
      {
        question: "Our premises are listed. Does that affect how we treat the costs?",
        answer:
          "It can, significantly. Work on listed or conservation-area premises often splits between capital expenditure and allowable repairs, and the treatment changes what you can claim and when. It's worth talking to us before the work is commissioned — after the invoice arrives, the options are narrower.",
      },
      {
        question: "How far are you from Lichfield?",
        answer:
          "About ten minutes, in Amington on the Tamworth side. We're happy to come to you, and plenty of Lichfield clients prefer to call in on us. Everything runs in Xero either way, so you're never waiting on a meeting to see your numbers.",
      },
      {
        question: "What does 'unlimited support' actually mean?",
        answer:
          "You can pick up the phone or email whenever you have a question, with no clock running and no invoice afterwards. It's included in the fixed monthly fee, because a question you don't ask because you're worried about the cost is the one that ends up costing you.",
      },
      {
        question: "Can you take over from our current accountant mid-year?",
        answer:
          "Yes, and we do it regularly. We handle the handover, get the records straight, and tell you honestly if anything needs putting right before we rely on it.",
      },
    ],
  },
  {
    slug: "accountants-solihull",
    town: "Solihull",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Solihull",
    metaDescription:
      "Xero accountants and outsourced finance for Solihull businesses. Bookkeeping, VAT, payroll, management accounts and strategic advice from a Xero Platinum Partner. More than numbers.",
    intro: [
      "Solihull is one of the strongest business economies in the West Midlands — home to the NEC, Birmingham Business Park, Blythe Valley Park and a dense community of professional services firms, consultancies and technology businesses. It's exactly the kind of ambitious environment where a proactive finance partner makes a real difference.",
      "We support Solihull directors and owner-managers as their outsourced finance team: accurate cloud bookkeeping, tax handled properly, and the management information and forecasting you need to make confident decisions. As a Xero Platinum Partner, we combine leading technology with genuinely responsive, senior support.",
      // Merged in from /bookkeeping-services-solihull on 26 October 2026, which
      // now redirects here. Kept because it is genuine local copy about the
      // bookkeeping work, not a duplicate of the paragraphs above.
      "Bookkeeping is where that starts. From Blythe Valley to Birmingham Business Park, Solihull's consultancies and growing firms rely on accurate books to plan and grow, so we keep your bookkeeping reconciled and real-time in Xero. Properly maintained books feed the financial reports and forecasting we provide, which means your Solihull business always makes decisions from solid numbers.",
    ],
    nearby: ["Shirley", "Knowle", "Dorridge", "Olton", "Bickenhill", "Blythe Valley"],
    faqs: [
      {
        question: "Do you work with businesses near the NEC and Birmingham Business Park?",
        answer:
          "Yes. Solihull's business parks — Birmingham Business Park, Blythe Valley and the NEC area — are home to many of the consultancies and growing firms we support with outsourced finance and Xero.",
      },
      {
        question: "Can bookkeeping feed into management accounts for my Solihull business?",
        answer:
          "Yes. We keep your Solihull books accurate in Xero and use them to produce real-time management accounts and forecasts, so the bookkeeping is doing more than satisfying HMRC.",
      },
      {
        question: "Can you provide management accounts and forecasting?",
        answer:
          "Absolutely. Beyond day-to-day compliance, we give Solihull businesses regular management accounts, cash flow forecasting and quarterly reviews so you can plan and grow with confidence.",
      },
      {
        question: "Do I need to be based in Solihull to work with you?",
        answer:
          "No. We're based in nearby Tamworth and support Solihull businesses seamlessly through Xero and video calls, with in-person meetings whenever they help.",
      },
    ],
  },
  {
    slug: "accountants-cannock",
    town: "Cannock",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Cannock",
    h1: "Accountants in Cannock",
    metaTitle: "Accountants in Cannock — Bookkeeping, VAT and Payroll",
    metaDescription:
      "Accountants for Cannock businesses — logistics, trades, retail and services. A Xero Platinum Partner handling bookkeeping, VAT, payroll and financial reports, on fixed monthly fees with a named contact.",
    opening:
      "CASS works with Cannock businesses — logistics and distribution, trades, retail and professional services — handling bookkeeping, VAT, payroll and the financial reporting that comes after them. We are a Xero Platinum Partner based a short drive away in Amington, Tamworth, working with Cannock clients in person and remotely, on fixed monthly fees with a named contact.",
    sections: [
      {
        heading: "What Cannock businesses come to us for",
        body: ["A logistics or trades business usually arrives with payroll and VAT at volume; a retailer arrives with reconciliation and margin. The order here follows that."],
        items: [
          { label: "Bookkeeping", to: "/services/bookkeeping", note: "Reconciled and current in Xero, which is where everything else starts." },
          { label: "VAT and MTD for VAT", to: "/services/vat", note: "Returns prepared and filed on time, with the right scheme for how you actually trade." },
          { label: "Payroll", to: "/services/payroll", note: "Weekly or monthly, including auto-enrolment, which matters when you have drivers, warehouse or retail staff on shifts." },
          { label: "Limited Company Year End Accounts", to: "/services/ltd-co-accounts", note: "Filed to deadline and explained before filing." },
          { label: "Financial Reports", to: "/services/management-accounts-financial-reporting", note: "Monthly or quarterly management accounts showing margin by job, contract or site." },
          { label: "Cashflow Forecasting", to: "/services/cash-flow-forecasting", note: "For businesses where payment terms, fuel and wages all land in a different week to the money coming in." },
          { label: "Outsourced Finance and Virtual FD", to: "/services/outsourced-finance", note: "When the finance work has outgrown the person currently doing it alongside their real job." },
          { label: "Moving to Xero", to: "/services/set-up-and-implementation", note: "Migration handled properly, history brought across." },
        ],
      },
      {
        heading: "Where we are, plainly",
        body: [
          "We do not have a Cannock office, and we are not going to pretend otherwise. We are in Amington, Tamworth — a short drive up the A5 — and we work with Cannock businesses in three ways: at your premises, at our office, or in live Xero with video calls for the reviews.",
          "Most of the work happens in Xero, which means you and we are looking at the same numbers at the same time rather than posting records back and forth.",
        ],
      },
      {
        heading: "Who we are a good fit for",
        body: [
          "Growing Cannock businesses that want an ongoing finance relationship rather than a once-a-year filing. We do not normally provide compliance in isolation — filing returns for records nobody maintains helps no one — with ACSP identity verification the stated exception.",
        ],
      },
    ],
    intro: [
      "Sitting on the M6 and A5 corridor beside Cannock Chase, Cannock has built a busy economy around logistics, distribution, retail (including McArthurGlen Designer Outlet West Midlands) and a strong base of trades and independent businesses. These are exactly the kinds of businesses that benefit from tight bookkeeping and real-time numbers.",
      "We help Cannock businesses stay on top of VAT, payroll and cash flow, with cloud accounting that gives you visibility whenever you need it — not accounts that arrive months too late. Fixed monthly fees, a dedicated contact, and advice that's there when you're making decisions.",
    ],
    nearby: ["Hednesford", "Rugeley", "Norton Canes", "Heath Hayes", "Bridgtown", "Cannock Chase"],
    faqs: [
      {
        question: "Do you work with logistics and trades businesses?",
        answer:
          "Yes. Cannock's location on the M6/A5 means logistics, distribution and trades are a big part of the local economy — and a natural fit for our bookkeeping, VAT and payroll support.",
      },
      {
        question: "Can you help me get on top of VAT and payroll?",
        answer:
          "Definitely. We handle VAT returns and payroll for Cannock businesses so they're always accurate and on time, with cloud accounting keeping everything up to date.",
      },
      {
        question: "Are you local to Cannock?",
        answer:
          "We're based in nearby Amington, Tamworth, a short drive away, and support Cannock businesses both in person and remotely through Xero and video calls.",
      },
      {
        question: "How do you work with a business that never comes to your office?",
        answer:
          "Most of it happens in Xero, so we both see the same numbers at the same time, with video calls for the reviews. We'll come to you, or you to Amington, whenever meeting in person is genuinely the better way.",
      },
      {
        question: "We run a fleet and shift payroll. Is that a problem?",
        answer:
          "No. Payroll at volume, including auto-enrolment, starters and leavers, is routine work for us — and it connects to the reporting rather than sitting apart from it.",
      },
    ],
  },
  {
    slug: "accountants-hinckley",
    town: "Hinckley",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Hinckley",
    metaDescription:
      "Xero accountants and outsourced finance for Hinckley businesses — logistics, manufacturing, retail and services. Bookkeeping, VAT, payroll and proactive business advice.",
    intro: [
      "Hinckley sits at the heart of England's 'Golden Triangle' of logistics, close to the M69, M1 and M6, with major distribution and manufacturing alongside a proud hosiery and textiles heritage and a revitalised town centre around The Crescent. It's a hard-working business community that values reliability and precision.",
      "We help Hinckley businesses understand their true profitability, tighten cash flow and plan ahead, with real-time cloud accounting and proactive, year-round support. Fixed monthly fees, a dedicated day-to-day contact, and genuinely useful business insight — more than numbers.",
    ],
    nearby: ["Burbage", "Barwell", "Earl Shilton", "Sapcote", "Stoke Golding", "Nuneaton"],
    faqs: [
      {
        question: "Do you work with logistics and manufacturing businesses in Hinckley?",
        answer:
          "Yes. Hinckley's position in the logistics 'Golden Triangle' and its manufacturing base fit well with how we work — including stock, job costing and the margins that matter to these sectors.",
      },
      {
        question: "Can you help with cash flow and profitability?",
        answer:
          "That's central to how we work. We give Hinckley businesses real-time cash flow visibility and clear reporting on profitability, so you can plan ahead rather than react.",
      },
      {
        question: "How does working with an accountant further away work?",
        answer:
          "Seamlessly. We support Hinckley businesses through Xero and video calls with a dedicated contact you can always reach — and we're a straightforward drive away in Tamworth when meeting in person helps.",
      },
    ],
  },
  {
    slug: "accountants-walsall",
    town: "Walsall",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Walsall",
    metaDescription:
      "Accountants and outsourced finance for the Walsall borough — succession planning, property held in the company and management information for long-established family manufacturers.",
    intro: [
      "The Walsall borough is bigger and more varied than the name suggests — Willenhall, Darlaston, Bloxwich, Aldridge and Pelsall all sit within it, each with its own character. What ties the business base together is longevity: a great many of the companies here are second, third or even fourth generation, in metal forming, locks, fabrication and engineering.",
      "That longevity creates a particular set of questions, and they are rarely the ones a standard year-end service answers. The first is succession. Where a business is passing to the next generation or to a long-standing employee, what the company is genuinely worth, how the transfer is structured and what it costs in tax on both sides need working through years ahead of the event, not in the months before it.",
      "The second is property. It is very common around Walsall for the trading company to own its own industrial premises, often bought decades ago for a fraction of today's value. That has real consequences — for how the business is valued, for the tax position on any eventual sale, and for whether the property ought to sit in the trading company at all or in a separate structure. It is a conversation worth having deliberately rather than discovering the answer at the point of sale.",
      "The third is simply information. A company can be forty years old and still not know which of its customers are profitable. Monthly management accounts with margin by customer and by job usually settle that argument quickly. We are around fourteen miles away near Tamworth, and everything runs in Xero as a Platinum Partner.",
    ],
    nearby: ["Willenhall", "Darlaston", "Bloxwich", "Aldridge", "Pelsall", "Streetly"],
    faqs: [
      {
        question: "We're passing the business to the next generation. When should we start planning?",
        answer:
          "Sooner than most people do — ideally several years out. The valuation, the structure of the transfer, whether it's a share sale, a gradual buy-out or a holding company, and the tax position for both generations all interact. With years of runway you have genuine choices; with months you're largely taking what you're given.",
      },
      {
        question: "Our company owns its factory. Is that a problem?",
        answer:
          "Not a problem, but it deserves a deliberate decision rather than inertia. Property bought decades ago and now worth many times its cost affects how the business is valued, complicates a future sale of the trade, and may sit better in a separate structure. The options narrow considerably once a sale is in progress.",
      },
      {
        question: "We've been trading forty years and still don't know which customers make money. Can you tell us?",
        answer:
          "Yes, and it's often the quickest win. Monthly management accounts with margin by customer and by job usually confirm what the owner suspected about half the time — and the other half is where the real opportunity is.",
      },
      {
        question: "Are you based in Walsall?",
        answer:
          "No — we're in Amington, Tamworth, about fourteen miles away. We'll come to you when a face-to-face is genuinely worth it, and everything else runs through Xero so you're not waiting on a meeting to see your numbers.",
      },
    ],
  },
  {
    slug: "accountants-coleshill",
    town: "Coleshill",
    county: "Warwickshire",
    headline: "Accountants & Outsourced Finance in Coleshill",
    metaDescription:
      "Local accountants and outsourced finance for Coleshill businesses. Xero Platinum Partner offering bookkeeping, VAT, payroll, tax and proactive business advice near the M6/M42.",
    intro: [
      "Sitting at the junction of the M6, M42 and A446 — right by Birmingham Airport and the NEC — Coleshill is a small Warwickshire town with a big strategic location, home to logistics operators, professional firms and independent businesses that value being well connected.",
      "We help Coleshill businesses stay on top of the numbers with real-time cloud accounting, proper VAT and payroll, and advice that's there when you need to make a decision. Fixed monthly fees, a dedicated day-to-day contact and genuinely proactive support.",
    ],
    nearby: ["Water Orton", "Curdworth", "Maxstoke", "Shustoke", "Marston Green", "Kingsbury"],
    faqs: [
      {
        question: "Do you work with logistics and transport businesses near the M6/M42?",
        answer:
          "Yes. Coleshill's location by the motorway network and Birmingham Airport makes logistics and transport a big part of the local economy — a natural fit for our bookkeeping, VAT and payroll support.",
      },
      {
        question: "How close are you to Coleshill?",
        answer:
          "We're a short drive away in Tamworth, so we're genuinely local to Coleshill and happy to meet in person or work remotely through Xero — whichever suits you.",
      },
      {
        question: "Do you offer fixed monthly fees?",
        answer:
          "Yes — fixed monthly fees tailored to what your Coleshill business actually needs, with no surprise bills. Our pricing is published, so you can build your package on the pricing page and see the figure yourself in about two minutes.",
      },
    ],
  },
  {
    slug: "accountants-swadlincote",
    town: "Swadlincote",
    county: "Derbyshire",
    headline: "Accountants & Outsourced Finance in Swadlincote",
    metaDescription:
      "Xero accountants and outsourced finance for Swadlincote businesses — manufacturing, ceramics heritage, retail and trades. Bookkeeping, VAT, payroll and proactive business advice.",
    intro: [
      "On the edge of the National Forest in South Derbyshire, Swadlincote grew up around coal, ceramics and pipe-making and today supports a busy mix of manufacturing, retail, trades and independent businesses. It's a close-knit community that values a straightforward, reliable finance partner.",
      "We help Swadlincote businesses understand their numbers, tighten cash flow and stay compliant, with cloud accounting that gives you visibility whenever you need it. Fixed monthly fees, a dedicated contact and proactive, year-round support — more than numbers.",
    ],
    nearby: ["Church Gresley", "Newhall", "Woodville", "Midway", "Hartshorne", "Overseal"],
    faqs: [
      {
        question: "Do you work with small businesses and trades in Swadlincote?",
        answer:
          "Yes. Independent businesses, trades and manufacturers in and around Swadlincote are exactly who we love to help, whether you're a sole trader or a limited company.",
      },
      {
        question: "Can you help me keep on top of VAT and cash flow?",
        answer:
          "Definitely. We handle VAT and give Swadlincote businesses real-time cash flow visibility through cloud accounting, so you can plan ahead with confidence.",
      },
      {
        question: "How does working with an accountant in Tamworth work for a Swadlincote business?",
        answer:
          "Seamlessly. We support you through Xero and video calls with a dedicated contact you can always reach, and we're a straightforward drive away when meeting in person helps.",
      },
    ],
  },
  {
    slug: "accountants-rugeley",
    town: "Rugeley",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Rugeley",
    metaDescription:
      "Accountants for Rugeley businesses — supporting the trades and contractors working on the power station site redevelopment, plus the town's independents and small manufacturers.",
    intro: [
      "Rugeley is a town in the middle of a genuine transition, and that has a direct bearing on the businesses in it. The old power station site — for decades the thing the town was known for — is being redeveloped on a substantial scale, and the construction, groundworks, trades and supply businesses working on and around it are a real part of the local economy now.",
      "For a trade or contracting business working on a long redevelopment, the accounting priorities are specific. Retentions held back for months after the work is done. Applications for payment rather than simple invoices. CIS deductions on subcontractors. And a cash position that can look healthy on paper while the money sits in someone else's account. We forecast for retentions explicitly, because a growing contractor running out of cash is a more common story than a failing one.",
      "The rest of the town is a different picture: independent shops and services, small manufacturers in Brereton, and a good number of sole traders and one-van trades. For these the work is unglamorous and valuable — records that are up to date, VAT filed without drama, and knowing what the next quarter looks like.",
      "Rugeley sits in Cannock Chase District, so rates and licensing go through Cannock Chase Council rather than Lichfield or Stafford, which catches people out. We are about thirteen miles away in Amington, Tamworth — close enough to come to site when it is useful, and set up so that day to day it makes no difference.",
    ],
    nearby: ["Brereton", "Armitage", "Slitting Mill", "Etchinghill", "Handsacre", "Colton"],
    faqs: [
      {
        question: "We're a contractor with money held in retentions. How do we manage that?",
        answer:
          "By forecasting it separately from your debtor book, because it behaves completely differently — retentions can sit for six or twelve months after practical completion. We track them individually and build them into the cashflow forecast, so you can see what's genuinely available now versus what's promised later.",
      },
      {
        question: "Do you handle applications for payment rather than invoices?",
        answer:
          "Yes. Construction payment cycles work on applications, valuations and certificates rather than straightforward invoicing, and the accounting has to follow that — including how you recognise work done but not yet certified. We set it up so the reporting reflects the contract reality.",
      },
      {
        question: "Which council covers Rugeley?",
        answer:
          "Cannock Chase District Council, for business rates, licensing and planning — not Lichfield or Stafford, which is what a lot of people assume from the geography.",
      },
      {
        question: "We're a small independent, not a contractor. Are you the right fit?",
        answer:
          "Yes. Plenty of our Rugeley clients are independent shops, services and one-van trades. What they want is records that are current, VAT handled without a scramble and someone who answers the phone — and the fixed monthly fee is scaled accordingly.",
      },
    ],
  },
  {
    slug: "accountants-ashby-de-la-zouch",
    town: "Ashby-de-la-Zouch",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Ashby-de-la-Zouch",
    metaDescription:
      "Accountants for Ashby-de-la-Zouch businesses — A42 corridor distribution, market-town independents and National Forest leisure and tourism operators.",
    intro: [
      "Ashby-de-la-Zouch is an unusual mix, and it makes for interesting work. A handsome market town with a genuinely independent high street, sitting directly on the A42 corridor between the M42 and East Midlands Airport, in the middle of the National Forest. Three quite different sorts of business, all within a few miles of each other. We are about seventeen miles away near Tamworth.",
      "The A42 corridor brings distribution, warehousing and the professional firms that service them. For these businesses the work is about volume and margin — transaction levels that need automating rather than typing, and reporting that shows margin per customer or per contract rather than one annual figure.",
      "Market Street and the town centre are the opposite: independent retailers, restaurants, professional practices and services, where the numbers are smaller but the margins are tighter and cash timing is everything. Getting paid, managing stock sensibly and knowing what the quiet months look like matters more than sophisticated reporting.",
      "Then there is leisure and tourism. The National Forest, Ashby Castle, Conkers and the canal bring visitors, and visitor-driven businesses have a cashflow shape all of their own — strong school holidays, thin shoulder months, and capital spend that has to be timed around both. We forecast for that pattern specifically, and we make sure capital allowances on that spend are actually claimed, which in our experience is one of the more commonly missed reliefs in a leisure business.",
    ],
    nearby: ["Measham", "Moira", "Packington", "Blackfordby", "Woodville", "Smisby"],
    faqs: [
      {
        question: "We're a visitor-led business with a very seasonal year. Can you help?",
        answer:
          "Yes, and it needs forecasting built on your real pattern — school holidays, weather, shoulder months — rather than an annual figure divided by twelve. We'll show you the thin months well ahead and help you time capital spend and staffing around them instead of into them.",
      },
      {
        question: "We're an independent shop, not a big company. Are we too small?",
        answer:
          "No. A good share of our clients are independent retailers and small practices. What changes is the emphasis: getting paid, sensible stock levels and knowing what the quiet months hold, rather than complex reporting. The fixed monthly fee is scaled to the work.",
      },
      {
        question: "We're a distribution business on the A42. Can you cope with our volumes?",
        answer:
          "Comfortably. High transaction volumes are an automation question — bank feeds, rules and invoice capture doing the routine coding — after which we report on margin by customer or contract rather than handing you one annual figure.",
      },
      {
        question: "Have we been claiming everything on our capital spend?",
        answer:
          "It's worth checking. Fit-out, plant, equipment and vehicles often attract more relief than businesses realise, and in leisure and hospitality especially we regularly find allowances that were never claimed. We'll review your recent spend as part of taking you on.",
      },
    ],
  },
  {
    slug: "accountants-burntwood",
    town: "Burntwood",
    county: "Staffordshire",
    headline: "Accountants & Outsourced Finance in Burntwood",
    metaDescription:
      "Local accountants and outsourced finance for Burntwood businesses. Xero Platinum Partner offering bookkeeping, VAT, payroll, tax and proactive advice near Lichfield & Cannock Chase.",
    intro: [
      "Sitting between Lichfield and Cannock Chase, Burntwood is one of Staffordshire's larger towns, with a strong base of trades, home-based businesses, independent retailers and small firms serving the local community.",
      "We give Burntwood businesses a genuine alternative to the traditional accountant: fixed monthly fees, unlimited support and cloud accounting that keeps your numbers up to date, with advice that's there when you need to make a decision — not months afterwards.",
    ],
    nearby: ["Chase Terrace", "Chasetown", "Hammerwich", "Norton Canes", "Lichfield", "Boney Hay"],
    faqs: [
      {
        question: "Do you work with sole traders and home-based businesses in Burntwood?",
        answer:
          "Yes. Sole traders, home-based businesses and small firms in Burntwood are exactly who we love to help — with straightforward bookkeeping, tax and proactive advice.",
      },
      {
        question: "How local are you to Burntwood?",
        answer:
          "We're close by in Tamworth, just beyond Lichfield, so we're genuinely local to Burntwood and happy to meet in person or work remotely through Xero.",
      },
      {
        question: "What does 'unlimited support' mean?",
        answer:
          "It means you can pick up the phone or email whenever you have a question — with no clock running. For Burntwood clients on fixed monthly fees, advice is always included.",
      },
    ],
  },
  {
    slug: "accountants-bedworth",
    town: "Bedworth",
    county: "Warwickshire",
    headline: "Accountants & Outsourced Finance in Bedworth",
    metaDescription:
      "Accountants for Bedworth's family-run businesses, trades and small manufacturers — getting the basics right, planning for the next generation and reporting you can actually use.",
    intro: [
      "Bedworth's business base is not glamorous and does not need to be: family-run trades, small manufacturers and engineering firms around Bayton Road and Exhall, and independents serving the town. A lot of these companies are second or third generation, and that shapes what they actually need from an accountant.",
      "The first thing is usually unglamorous too — getting the basics genuinely right. Records that are up to date rather than reconstructed each January, VAT that is filed without a scramble, payroll that runs on time, and a set of numbers the owner can look at without having to interpret them. It sounds modest. For a business that has spent years finding out how the last year went eleven months later, it changes how decisions get made.",
      "The second is succession, and it comes up more often in Bedworth than almost anywhere else we work. Where a business is being handed to a son or daughter, or a long-standing employee is being brought in, the numbers matter enormously — what the company is genuinely worth, how the transfer is structured, what it means for tax on both sides, and whether it can support two households during the handover. These are conversations worth having years before the event, not months.",
      "Bedworth shares its borough with Nuneaton, so rates, licensing and planning all go through Nuneaton and Bedworth Borough Council — which catches out businesses moving in from Coventry or Warwickshire proper. We are about fifteen miles away near Tamworth, and everything runs in the cloud so distance makes no odds day to day.",
    ],
    nearby: ["Exhall", "Ash Green", "Collycroft", "Bulkington", "Keresley", "Bayton Road"],
    faqs: [
      {
        question: "We're a family business thinking about handing over. Where do we start?",
        answer:
          "Earlier than most people do. Start with what the business is genuinely worth and whether it can support both generations through the handover. Then the structure — share transfer, gradual buy-out, holding company — because that drives the tax position for everyone involved. Years ahead of the event you have options; months ahead you have far fewer.",
      },
      {
        question: "Our bookkeeping is always behind. Can you sort it out?",
        answer:
          "Yes, and it's the most common thing we take on here. We bring the records up to date, get the bank feeds and rules working so they stay that way, and then you're looking at current numbers instead of a reconstruction. It's the foundation for everything else — there's no point forecasting from figures you don't trust.",
      },
      {
        question: "Which council do we deal with in Bedworth?",
        answer:
          "Nuneaton and Bedworth Borough Council for rates, licensing and planning. It surprises businesses relocating from Coventry or elsewhere in Warwickshire, so it's worth checking before you assume.",
      },
      {
        question: "Do you only work with bigger companies?",
        answer:
          "No. A good share of our clients are one or two-van trades and small family firms, and the fixed monthly fee is scaled to the work. You'll know what you're paying before you commit.",
      },
    ],
  },
  {
    slug: "accountants-coalville",
    town: "Coalville",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Coalville",
    metaDescription:
      "Xero accountants and outsourced finance for Coalville businesses — logistics, distribution, manufacturing and trades near the M1. Bookkeeping, VAT, payroll and business advice.",
    intro: [
      "Built on coal and now firmly part of the National Forest, Coalville sits beside the M1 (Junction 22) at the heart of a major distribution and logistics belt, with manufacturing, warehousing and a strong base of trades and independent businesses.",
      "We help Coalville businesses keep tight control of their numbers — accurate bookkeeping, VAT and payroll, and real-time visibility of margins and cash flow. Fixed monthly fees, a dedicated contact and proactive advice that helps you make better decisions.",
    ],
    nearby: ["Whitwick", "Ellistown", "Ibstock", "Ravenstone", "Bardon", "Hugglescote"],
    faqs: [
      {
        question: "Do you work with logistics and distribution businesses near the M1?",
        answer:
          "Yes. Coalville's position by the M1 makes logistics, warehousing and distribution a big part of the local economy — a natural fit for our bookkeeping, VAT and payroll support.",
      },
      {
        question: "Can you give me real-time numbers rather than year-old accounts?",
        answer:
          "Definitely. With cloud accounting, Coalville businesses get up-to-date figures whenever they need them, not accounts that arrive months after year-end.",
      },
      {
        question: "How does working with an accountant in Tamworth work for a Coalville business?",
        answer:
          "Seamlessly. We support you through Xero and video calls with a dedicated contact you can always reach, and we're a straightforward drive away when meeting in person helps.",
      },
    ],
  },
  {
    slug: "accountants-brownhills",
    town: "Brownhills",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Brownhills",
    metaDescription:
      "Local accountants and outsourced finance for Brownhills businesses — retail, trades and small firms near Chasewater. Xero Platinum Partner offering bookkeeping, VAT and payroll.",
    intro: [
      "On the edge of Walsall beside Chasewater Country Park, Brownhills is a canal-side market town with a busy high street, retail and a strong base of trades and small independent businesses serving the local community.",
      "We help Brownhills businesses take the hassle out of their finances: accurate bookkeeping, VAT and payroll done properly, and clear numbers you can actually use. Fixed monthly fees, a dedicated day-to-day contact and advice that's there all year round.",
    ],
    nearby: ["Walsall Wood", "Clayhanger", "Pelsall", "Norton Canes", "Aldridge", "Chasetown"],
    faqs: [
      {
        question: "Do you work with sole traders and small businesses in Brownhills?",
        answer:
          "Yes. Sole traders, trades and small independent businesses in Brownhills are exactly who we love to help, whether you're a sole trader or a limited company.",
      },
      {
        question: "Can you handle VAT, payroll and tax together?",
        answer:
          "Absolutely. We can look after everything for your Brownhills business in one place — bookkeeping, VAT, payroll and tax — with cloud accounting keeping it all up to date.",
      },
      {
        question: "How local are you to Brownhills?",
        answer:
          "We're a short drive away in Tamworth and support Brownhills businesses both in person and remotely through Xero and video calls.",
      },
    ],
  },
  {
    slug: "accountants-west-bromwich",
    town: "West Bromwich",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in West Bromwich",
    metaDescription:
      "Xero accountants and outsourced finance for West Bromwich businesses — manufacturing, engineering, retail and services. Bookkeeping, VAT, payroll and proactive business advice.",
    intro: [
      "At the heart of the Black Country in Sandwell, West Bromwich pairs a proud manufacturing and engineering heritage with a revitalised town centre and a diverse community of retailers, service firms and ambitious SMEs.",
      "We act as the outsourced finance team for West Bromwich businesses: accurate cloud bookkeeping, VAT and payroll handled properly, and clear management information so you understand your margins and cash flow. Fixed monthly fees, a dedicated contact and genuinely proactive support.",
    ],
    nearby: ["Wednesbury", "Oldbury", "Smethwick", "Tipton", "Great Barr", "Hamstead"],
    faqs: [
      {
        question: "Do you work with manufacturing and engineering businesses in West Bromwich?",
        answer:
          "Yes. West Bromwich's manufacturing, engineering and trades businesses are a natural fit — we're comfortable with stock, job costing and the margins that matter to these sectors.",
      },
      {
        question: "Will I have a dedicated contact?",
        answer:
          "Always. You'll have a dedicated day-to-day contact who knows your West Bromwich business — never a call centre.",
      },
      {
        question: "Do I need to be based in West Bromwich to work with you?",
        answer:
          "No. We support West Bromwich businesses seamlessly through Xero and video calls, with in-person meetings whenever they help.",
      },
    ],
  },
  {
    slug: "accountants-wolverhampton",
    town: "Wolverhampton",
    county: "West Midlands",
    headline: "Accountants & Outsourced Finance in Wolverhampton",
    metaDescription:
      "Outsourced finance and management accounts for Wolverhampton businesses — supporting engineering, aerospace supply chain and growing manufacturers who need finance capability, not just compliance.",
    intro: [
      "We work with businesses across Wolverhampton, and we will start with the honest bit: we are about twenty-four miles away, near Tamworth. We are not local to Wolverhampton and we are not going to claim to be. What we are is an outsourced finance team that works perfectly well at that distance, and considerably cheaper than the equivalent capability hired in.",
      "The Wolverhampton businesses that come to us tend to have a particular problem. They are engineering or manufacturing companies — often in the aerospace or automotive supply chain, often around the i54 corridor, Bilston or Wednesfield — that have grown past the point where the owner can hold it all in their head. Turnover is up, the order book is healthy, and yet nobody can say with confidence which contracts are actually profitable.",
      "That is a management information problem rather than an accounting one, and it is what we are for. Monthly management accounts with contract-level margin, a rolling cashflow forecast, and a conversation each month about what the numbers are telling you. For a company with a long order book and money tied up in work in progress, knowing your cash position eight weeks out is worth more than any amount of retrospective compliance work.",
      "The supply chain adds its own pressure. Tier-one customers pay on their terms, not yours, and a growing order book can drain cash faster than a shrinking one. We forecast for that specifically, because 'profitable but out of cash' is a real way for a good Wolverhampton engineering business to get into difficulty.",
    ],
    nearby: ["Bilston", "Wednesfield", "Tettenhall", "Willenhall", "Penn", "Codsall"],
    faqs: [
      {
        question: "Are you based in Wolverhampton?",
        answer:
          "No. We're in Amington, Tamworth, roughly twenty-four miles away, and we'd rather say so plainly than imply we're round the corner. Everything runs through Xero and video call, and we travel over when a meeting genuinely warrants it.",
      },
      {
        question: "We're growing but we don't know which contracts make money. Can you tell us?",
        answer:
          "Yes, and it's usually the first thing we fix. With costs coded to contracts we produce monthly management accounts showing margin per job — labour, materials, bought-in parts and subcontractors. Most owners find their instinct is right about three-quarters of the time, and the other quarter is where the money is.",
      },
      {
        question: "Our customers pay on long terms. How do we manage the cash?",
        answer:
          "With a rolling forecast built around your actual order book and payment terms, not a generic template. Growth consumes cash in a supply chain business — you buy materials and pay wages long before a tier-one customer settles. Seeing that eight to twelve weeks ahead is what lets you plan facilities rather than react to them.",
      },
      {
        question: "Do we need to change accounting software?",
        answer:
          "Not necessarily, though we work best in Xero and will tell you if your current system is the thing holding the reporting back. If a move makes sense we'll handle it properly, with balances reconciled and the old system kept available.",
      },
    ],
  },
  {
    slug: "accountants-leicester",
    town: "Leicester",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Leicester",
    metaDescription:
      "Outsourced finance team and Xero Platinum Partner for Leicester businesses — manufacturing, textiles, distribution, tech and professional services. Bookkeeping, VAT, payroll and advice.",
    intro: [
      "One of the UK's most diverse and dynamic cities, Leicester has a thriving economy spanning textiles and manufacturing, distribution, technology, retail and professional services, supported by two universities and a strong entrepreneurial culture.",
      "We help Leicester founders and directors move beyond compliance: understanding margins, forecasting cash flow and planning investment, with a dedicated contact and senior expertise on hand. Real-time cloud accounting, fixed monthly fees and proactive advice.",
    ],
    nearby: ["Oadby", "Wigston", "Braunstone", "Glenfield", "Birstall", "Leicester Forest East"],
    faqs: [
      {
        question: "Do you work with growing and ambitious Leicester businesses?",
        answer:
          "Yes. From manufacturers and distributors to tech firms and professional services, we support ambitious Leicester businesses with outsourced finance, management accounts and strategic advice.",
      },
      {
        question: "Will I get a boutique service or a call centre?",
        answer:
          "A boutique, hands-on service. You'll have a dedicated contact who knows your Leicester business, backed by senior financial expertise.",
      },
      {
        question: "Do I need to be in central Leicester to work with you?",
        answer:
          "Not at all. We support businesses right across Leicester and Leicestershire through Xero and video calls, with in-person meetings whenever they help.",
      },
    ],
  },
  {
    slug: "accountants-castle-donington",
    town: "Castle Donington",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Castle Donington",
    metaDescription:
      "Accountants for Castle Donington businesses — 24/7 shift payroll, seasonal air-freight peaks and job costing for logistics, aviation services and motorsport engineering.",
    intro: [
      "Castle Donington's economy is dominated by two things that do not stop: East Midlands Airport and the freight moving through it. Add Donington Park and the motorsport engineering around it, and you have a business community with genuinely unusual accounting needs. We are about twenty-five miles away near Tamworth — not local, and we will not suggest otherwise.",
      "The most immediate difference is payroll. Air-side and freight operations run around the clock, which means shift patterns, night and weekend premiums, unsocial-hours uplifts and agency cover at peaks. Holiday pay has to be calculated on average earnings including those premiums rather than on basic hours, which is where a great many operators get it wrong and end up correcting it retrospectively. We set it up so it is right every run.",
      "The second is seasonality, and it is sharper here than in most places. Air freight has pronounced peaks, and a business geared for the autumn and pre-Christmas surge carries the cost base through the quiet months either side. Forecasting for that specifically — rather than dividing the year by twelve — is what stops a profitable operation running short of cash in February.",
      "For motorsport and precision engineering the question is different again: what did that build actually cost against what we quoted. We set up job-level costing so material, machine time, labour and bought-in parts land against each job. On skilled work quoted at a fixed price, that is the number that determines whether the year works.",
    ],
    nearby: ["Kegworth", "Diseworth", "Lockington", "Hemington", "Isley Walton", "East Midlands Gateway"],
    faqs: [
      {
        question: "We run 24/7 shifts. Can you handle the payroll?",
        answer:
          "Yes, and it's one of the main reasons Castle Donington businesses come to us. Night and weekend premiums, unsocial-hours uplifts and overtime at different rates are set up as separate pay elements, so payslips are clear and — importantly — holiday pay is calculated on average earnings including those premiums rather than on basic hours.",
      },
      {
        question: "Our trade peaks hard in the autumn. How do we plan for the quiet months?",
        answer:
          "With a forecast built on your actual seasonal pattern rather than an annual figure split twelve ways. Air-freight peaks are pronounced, and the cost base doesn't shrink in February. Seeing the trough eight to twelve weeks out is what turns it into a plan instead of a problem.",
      },
      {
        question: "Can you tell us what each engineering job really cost?",
        answer:
          "That's job costing, and for fixed-price skilled work it's the number that matters. We code material, machine time, labour and bought-in parts to each job so you can compare quoted against actual while the work is still live.",
      },
      {
        question: "Are you local to Castle Donington?",
        answer:
          "No — we're in Amington, Tamworth, about twenty-five miles away. We work with Castle Donington clients entirely through Xero and video call, and come over when there's a genuine reason to. We'd rather be straight about the distance than pretend.",
      },
    ],
  },
  {
    slug: "accountants-market-bosworth",
    town: "Market Bosworth",
    county: "Leicestershire",
    headline: "Accountants & Outsourced Finance in Market Bosworth",
    metaDescription:
      "Local accountants and outsourced finance for Market Bosworth businesses — rural enterprises, tourism, professional services and trades. Xero bookkeeping, VAT, payroll and advice.",
    intro: [
      "A picturesque historic market town famous for the Battle of Bosworth, Market Bosworth is home to a close-knit community of rural enterprises, tourism and hospitality businesses, professional firms and independent traders across west Leicestershire.",
      "We help Market Bosworth businesses simplify their finances with real-time cloud accounting, proper VAT and payroll, and advice that's there when you need to make a decision. Fixed monthly fees, a dedicated day-to-day contact and genuinely proactive support.",
    ],
    nearby: ["Sutton Cheney", "Carlton", "Barlestone", "Newbold Verdon", "Cadeby", "Shenton"],
    faqs: [
      {
        question: "Do you work with rural businesses, hospitality and independent traders?",
        answer:
          "Yes. Market Bosworth's rural enterprises, tourism and hospitality businesses, professional firms and independent traders are exactly who we love to help.",
      },
      {
        question: "Can you handle everything from bookkeeping to tax?",
        answer:
          "Absolutely. We can look after everything for your Market Bosworth business in one place — bookkeeping, VAT, payroll, year-end accounts and tax.",
      },
      {
        question: "How does working with a Tamworth-based accountant work?",
        answer:
          "Seamlessly. We support Market Bosworth businesses through Xero and video calls with a dedicated contact you can always reach, and we're a drive away when meeting in person helps.",
      },
    ],
  },
];

// Towns shown in the site footer's "Areas we serve" list. All towns are still
// discoverable via the /areas-we-support hub and the XML sitemap — the footer is
// curated to the primary target towns so it stays readable.
export const PRIMARY_LOCATION_SLUGS = [
  "accountants-tamworth",
  "accountants-sutton-coldfield",
  "accountants-lichfield",
  "accountants-birmingham",
  "accountants-solihull",
  "accountants-cannock",
  "accountants-burton-upon-trent",
  "accountants-nuneaton",
  "accountants-hinckley",
  "accountants-atherstone",
];

export const getLocation = (slug) => LOCATIONS.find((l) => l.slug === slug);
